import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Logo}.
 *
 * All sizing classes are Tailwind height utilities; no hardcoded values.
 *
 * @remarks
 * - Supports four sizes: sm (h-6), md (h-8, default), lg (h-10), xl (h-12)
 *
 * @example
 * ```tsx
 * const classes = logoVariants({ size: 'md' })
 * // Returns: "inline-flex h-8"
 * ```
 */
export const logoVariants = cva('inline-flex', {
	variants: {
		size: {
			sm: 'h-6',
			md: 'h-8',
			lg: 'h-10',
			xl: 'h-12',
		},
	},
	defaultVariants: { size: 'md' },
})

/**
 * Props for the {@link Logo} component.
 *
 * @property src - URL of the brand mark, supplied by the brand identity (never hardcoded here).
 * @property alt - Accessible name of the mark, supplied by the consumer.
 * @property size - Logo size: 'sm' (h-6) | 'md' (h-8, default) | 'lg' (h-10) | 'xl' (h-12)
 * @property className - Optional additional Tailwind classes
 *
 * @example
 * ```tsx
 * <Logo src={identity.logo.src} alt={identity.name} size="lg" />
 * ```
 */
export interface LogoProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof logoVariants> {
	src: string
	alt: string
}

/**
 * Brand-agnostic logo slot: renders whichever brand mark it is given.
 *
 * @remarks
 * - **Brand-agnostic (ADR-0045):** the mark and its accessible name come from
 *   the consumer — the brand identity bound above reusable UI — so the component
 *   never embeds a customer's name or colours.
 * - **Sizes:** Small (h-6), medium (h-8, default), large (h-10), xl (h-12); the
 *   image fills the height and keeps its aspect ratio.
 * - **Usage:** Application header, navigation, footer.
 *
 * @example
 * ```tsx
 * import { Logo } from '@today/ui-primitives'
 *
 * <Logo src={logoSrc} alt={brandName} size="lg" />
 * ```
 *
 * @see {@link logoVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/logo/logo.stories.tsx for visual examples
 */
export function Logo({ src, alt, size, className, ...props }: LogoProps) {
	return (
		<div {...props} className={cn(logoVariants({ size }), className)}>
			<img src={src} alt={alt} className="h-full w-auto" />
		</div>
	)
}
